import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDeployments, markAttendance, getAttendance,
  getTimesheets, generateTimesheet, submitTimesheet, approveTimesheet,
  applyLeave, getLeaveRequests, getLeaveBalances, reviewLeave
} from '../services/api';
import { CalendarCheck, Check, X, Clock, Plus, RefreshCw, FileText, Leaf } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const statusColor = (s) =>
  s === 'present'  ? 'bg-green-50 text-green-700' :
  s === 'absent'   ? 'bg-red-50 text-red-600' :
  s === 'half_day' ? 'bg-amber-50 text-amber-700' :
  s === 'leave'    ? 'bg-purple-50 text-purple-700' :
  s === 'holiday'  ? 'bg-blue-50 text-blue-700' :
  'bg-gray-50 text-gray-500';

const leaveStatusColor = (s) =>
  s === 'approved' ? 'bg-green-50 text-green-700' :
  s === 'rejected' ? 'bg-red-50 text-red-600' :
  'bg-amber-50 text-amber-700';

const tsStatusColor = (s) =>
  s === 'client_approved' ? 'bg-green-50 text-green-700' :
  s === 'submitted'       ? 'bg-blue-50 text-blue-700' :
  'bg-gray-100 text-gray-500';

// ── Mark Attendance Modal ─────────────────
const MarkAttendanceModal = ({ deployment, onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    deployment_id  : deployment.id,
    attendance_date: new Date().toISOString().slice(0, 10),
    status         : 'present',
    check_in       : '09:00',
    check_out      : '18:00',
    remarks        : ''
  });

  const mutation = useMutation({
    mutationFn: markAttendance,
    onSuccess: () => {
      toast.success('Attendance marked');
      queryClient.invalidateQueries(['attendance', deployment.id]);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to mark attendance')
  });

  const statusOptions = [
    { value: 'present',  label: 'Present',  color: 'bg-green-50 text-green-700 border-green-200' },
    { value: 'absent',   label: 'Absent',   color: 'bg-red-50 text-red-700 border-red-200' },
    { value: 'half_day', label: 'Half Day', color: 'bg-amber-50 text-amber-700 border-amber-200' },
    { value: 'leave',    label: 'Leave',    color: 'bg-purple-50 text-purple-700 border-purple-200' },
    { value: 'holiday',  label: 'Holiday',  color: 'bg-blue-50 text-blue-700 border-blue-200' },
    { value: 'week_off', label: 'Week Off', color: 'bg-gray-50 text-gray-600 border-gray-200' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Mark Attendance</h2>
            <p className="text-sm text-gray-400">{deployment.candidates?.first_name} {deployment.candidates?.last_name} — {deployment.clients?.company_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input type="date" value={form.attendance_date}
              onChange={e => setForm({ ...form, attendance_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="grid grid-cols-3 gap-2">
              {statusOptions.map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => setForm({ ...form, status: opt.value })}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border transition
                    ${form.status === opt.value ? opt.color + ' border-current' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {form.status === 'present' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check In</label>
                <input type="time" value={form.check_in}
                  onChange={e => setForm({ ...form, check_in: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Check Out</label>
                <input type="time" value={form.check_out}
                  onChange={e => setForm({ ...form, check_out: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <input value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })}
              placeholder="Optional remarks"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={() => mutation.mutate(form)} disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mutation.isPending ? 'Saving...' : 'Mark Attendance'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Apply Leave Modal ─────────────────────
const ApplyLeaveModal = ({ deployments, onClose }) => {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    candidate_id: '',
    leave_type  : 'casual',
    from_date   : '',
    to_date     : '',
    reason      : ''
  });

  const mutation = useMutation({
    mutationFn: applyLeave,
    onSuccess: () => {
      toast.success('Leave applied successfully');
      queryClient.invalidateQueries(['leave-requests']);
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to apply leave')
  });

  const uniqueCandidates = deployments.reduce((acc, dep) => {
    if (!acc.find(c => c.id === dep.candidate_id)) {
      acc.push({ id: dep.candidate_id, name: `${dep.candidates?.first_name} ${dep.candidates?.last_name}` });
    }
    return acc;
  }, []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-gray-900">Apply for Leave</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>

        <form onSubmit={e => { e.preventDefault(); mutation.mutate(form); }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Worker *</label>
            <select value={form.candidate_id} onChange={e => setForm({ ...form, candidate_id: e.target.value })} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select worker</option>
              {uniqueCandidates.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type *</label>
            <select value={form.leave_type} onChange={e => setForm({ ...form, leave_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="casual">Casual Leave</option>
              <option value="sick">Sick Leave</option>
              <option value="earned">Earned Leave</option>
              <option value="maternity">Maternity Leave</option>
              <option value="paternity">Paternity Leave</option>
              <option value="unpaid">Unpaid Leave</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">From *</label>
              <input type="date" value={form.from_date} onChange={e => setForm({ ...form, from_date: e.target.value })} required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">To *</label>
              <input type="date" value={form.to_date} onChange={e => setForm({ ...form, to_date: e.target.value })} required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Reason for leave..." />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={mutation.isPending}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mutation.isPending ? 'Submitting...' : 'Apply Leave'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Attendance History inline ─────────────
const AttendanceHistory = ({ deployment, month, year }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['attendance', deployment.id, month, year],
    queryFn : () => getAttendance(deployment.id, { month, year }).then(r => r.data.data)
  });

  if (isLoading) return <div className="text-center py-4 text-gray-400 text-sm">Loading...</div>;
  const summary = data?.summary || {};

  return (
    <div className="mt-4 border-t border-gray-100 pt-4">
      <div className="grid grid-cols-4 gap-3 mb-4">
        {[
          { label: 'Present',  value: summary.present,  color: 'text-green-600' },
          { label: 'Absent',   value: summary.absent,   color: 'text-red-600' },
          { label: 'Leave',    value: summary.leave,    color: 'text-purple-600' },
          { label: 'OT Hours', value: summary.total_overtime_hours?.toFixed(1) || 0, color: 'text-blue-600' }
        ].map(item => (
          <div key={item.label} className="text-center bg-gray-50 rounded-xl p-3">
            <div className={`text-xl font-semibold ${item.color}`}>{item.value ?? 0}</div>
            <div className="text-xs text-gray-400 mt-1">{item.label}</div>
          </div>
        ))}
      </div>
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {data?.records?.map(rec => (
          <div key={rec.id} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50">
            <span className="text-sm text-gray-600">{rec.attendance_date?.slice(0, 10)}</span>
            <div className="flex items-center gap-3">
              {rec.check_in && (
                <span className="text-xs text-gray-400">
                  <Clock size={11} className="inline mr-1" />
                  {rec.check_in?.slice(0, 5)} — {rec.check_out?.slice(0, 5)}
                </span>
              )}
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(rec.status)}`}>
                {rec.status?.replace('_', ' ')}
              </span>
            </div>
          </div>
        ))}
        {(!data?.records || data.records.length === 0) && (
          <p className="text-center py-4 text-gray-400 text-sm">No records for this month</p>
        )}
      </div>
    </div>
  );
};

// ── Leave Balances Panel ──────────────────
const LeaveBalancesPanel = ({ candidateId, name }) => {
  const year = new Date().getFullYear();
  const { data = [], isLoading } = useQuery({
    queryKey: ['leave-balances', candidateId, year],
    queryFn : () => getLeaveBalances(candidateId, year).then(r => r.data.data)
  });

  if (isLoading) return <div className="text-center py-3 text-gray-400 text-sm">Loading balances...</div>;
  if (!data.length) return <div className="text-center py-3 text-gray-400 text-sm">No leave balances found for {year}</div>;

  return (
    <div className="mt-4 border-t border-gray-100 pt-4">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Leave Balances — {year}</p>
      <div className="grid grid-cols-3 gap-2">
        {data.map(b => (
          <div key={b.id} className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs text-gray-500 capitalize">{b.leave_type?.replace('_', ' ')}</p>
            <p className="text-lg font-semibold text-gray-900 mt-1">{b.balance ?? (b.allocated - b.used)}</p>
            <p className="text-xs text-gray-400">{b.used}/{b.allocated} used</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main Attendance Page ──────────────────
const Attendance = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = ['super_admin', 'branch_manager'].includes(user?.role);

  const [tab,          setTab]          = useState('attendance');
  const [month,        setMonth]        = useState(new Date().getMonth() + 1);
  const [year,         setYear]         = useState(new Date().getFullYear());
  const [selectedDep,  setSelectedDep]  = useState(null);
  const [expandedDep,  setExpandedDep]  = useState(null);
  const [expandedMode, setExpandedMode] = useState('history'); // 'history' | 'balances'
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveFilter, setLeaveFilter]   = useState('pending');

  const { data: depData, isLoading: loadingDeps } = useQuery({
    queryKey: ['deployments-active'],
    queryFn : () => getDeployments({ status: 'active', limit: 100 }).then(r => r.data)
  });
  const deployments = depData?.data || [];

  const { data: leavesData, isLoading: loadingLeaves } = useQuery({
    queryKey: ['leave-requests', leaveFilter],
    queryFn : () => getLeaveRequests({ status: leaveFilter || undefined, limit: 50 }).then(r => r.data.data),
    enabled : tab === 'leaves'
  });

  const { data: tsData, isLoading: loadingTs } = useQuery({
    queryKey: ['timesheets', month, year],
    queryFn : () => getTimesheets({ month, year, limit: 50 }).then(r => r.data.data),
    enabled : tab === 'timesheets'
  });

  const reviewMutation = useMutation({
    mutationFn: ({ id, status, review_notes }) => reviewLeave(id, { status, review_notes }),
    onSuccess: () => { toast.success('Leave updated'); queryClient.invalidateQueries(['leave-requests']); },
    onError  : (err) => toast.error(err.response?.data?.message || 'Failed to update leave')
  });

  const generateMutation = useMutation({
    mutationFn: (depId) => generateTimesheet(depId, { month, year }),
    onSuccess: () => { toast.success('Timesheet generated'); queryClient.invalidateQueries(['timesheets']); },
    onError  : (err) => toast.error(err.response?.data?.message || 'Failed to generate timesheet')
  });

  const submitTsMutation = useMutation({
    mutationFn: submitTimesheet,
    onSuccess: () => { toast.success('Timesheet submitted'); queryClient.invalidateQueries(['timesheets']); },
    onError  : (err) => toast.error(err.response?.data?.message || 'Failed to submit')
  });

  const approveTsMutation = useMutation({
    mutationFn: approveTimesheet,
    onSuccess: () => { toast.success('Timesheet approved'); queryClient.invalidateQueries(['timesheets']); },
    onError  : (err) => toast.error(err.response?.data?.message || 'Failed to approve')
  });

  const toggleExpand = (depId, mode) => {
    if (expandedDep === depId && expandedMode === mode) {
      setExpandedDep(null);
    } else {
      setExpandedDep(depId);
      setExpandedMode(mode);
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Attendance & Leave</h1>
          <p className="text-sm text-gray-400 mt-0.5">Track attendance, manage leaves and timesheets</p>
        </div>
        <div className="flex gap-2">
          {tab === 'leaves' && (
            <button onClick={() => setShowLeaveModal(true)}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
              <Plus size={16} />
              Apply Leave
            </button>
          )}
          {tab !== 'leaves' && (
            <>
              <select value={month} onChange={e => setMonth(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
              <select value={year} onChange={e => setYear(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {[{ id: 'attendance', label: 'Attendance' },
          { id: 'leaves',     label: 'Leaves' },
          { id: 'timesheets', label: 'Timesheets' }
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition
              ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── ATTENDANCE TAB ── */}
      {tab === 'attendance' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              Active Deployments — {MONTHS[month - 1]} {year}
            </p>
          </div>

          {loadingDeps ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : deployments.length === 0 ? (
            <div className="text-center py-12">
              <CalendarCheck size={32} className="mx-auto text-gray-200 mb-3" />
              <p className="text-gray-400 text-sm">No active deployments</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {deployments.map(dep => (
                <div key={dep.id} className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 text-sm font-semibold">
                          {dep.candidates?.first_name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {dep.candidates?.first_name} {dep.candidates?.last_name}
                        </p>
                        <p className="text-xs text-gray-400">
                          {dep.clients?.company_name} · {dep.designation || 'Worker'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleExpand(dep.id, 'balances')}
                        className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">
                        {expandedDep === dep.id && expandedMode === 'balances' ? 'Hide Balances' : 'Leave Balances'}
                      </button>
                      <button onClick={() => toggleExpand(dep.id, 'history')}
                        className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">
                        {expandedDep === dep.id && expandedMode === 'history' ? 'Hide History' : 'View History'}
                      </button>
                      <button onClick={() => setSelectedDep(dep)}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                        <Check size={13} />
                        Mark Today
                      </button>
                    </div>
                  </div>

                  {expandedDep === dep.id && expandedMode === 'history' && (
                    <AttendanceHistory deployment={dep} month={month} year={year} />
                  )}
                  {expandedDep === dep.id && expandedMode === 'balances' && (
                    <LeaveBalancesPanel candidateId={dep.candidate_id}
                      name={`${dep.candidates?.first_name} ${dep.candidates?.last_name}`} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── LEAVES TAB ── */}
      {tab === 'leaves' && (
        <div className="space-y-3">
          {/* Filter */}
          <div className="flex gap-1 w-fit">
            {['pending', 'approved', 'rejected', ''].map((f) => (
              <button key={f} onClick={() => setLeaveFilter(f)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition
                  ${leaveFilter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                {f === '' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {loadingLeaves ? (
              <div className="text-center py-12 text-gray-400">Loading leave requests...</div>
            ) : !leavesData?.requests?.length ? (
              <div className="text-center py-16">
                <Leaf size={32} className="mx-auto text-gray-200 mb-3" />
                <p className="text-gray-400 text-sm">No leave requests found</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {['Worker', 'Type', 'From', 'To', 'Days', 'Reason', 'Status', isAdmin ? 'Actions' : ''].filter(Boolean).map(h => (
                      <th key={h} className="text-left text-xs font-medium text-gray-400 px-5 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {leavesData.requests.map(req => (
                    <tr key={req.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3 text-sm font-medium text-gray-900">
                        {req.candidates?.first_name} {req.candidates?.last_name}
                        <p className="text-xs text-gray-400 font-normal">{req.candidates?.employee_code}</p>
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600 capitalize">{req.leave_type?.replace('_', ' ')}</td>
                      <td className="px-5 py-3 text-sm text-gray-600">{req.from_date?.slice(0, 10)}</td>
                      <td className="px-5 py-3 text-sm text-gray-600">{req.to_date?.slice(0, 10)}</td>
                      <td className="px-5 py-3 text-sm font-medium text-gray-900">{req.days}</td>
                      <td className="px-5 py-3 text-sm text-gray-500 max-w-xs truncate">{req.reason || '—'}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${leaveStatusColor(req.status)}`}>
                          {req.status}
                        </span>
                      </td>
                      {isAdmin && (
                        <td className="px-5 py-3">
                          {req.status === 'pending' && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => reviewMutation.mutate({ id: req.id, status: 'approved' })}
                                disabled={reviewMutation.isPending}
                                className="text-xs px-2.5 py-1 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 font-medium">
                                Approve
                              </button>
                              <button
                                onClick={() => reviewMutation.mutate({ id: req.id, status: 'rejected' })}
                                disabled={reviewMutation.isPending}
                                className="text-xs px-2.5 py-1 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 font-medium">
                                Reject
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ── TIMESHEETS TAB ── */}
      {tab === 'timesheets' && (
        <div className="space-y-3">
          {/* Generate row */}
          <div className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Generate Timesheets</p>
                <p className="text-xs text-gray-400 mt-0.5">Create monthly timesheets from attendance records for {MONTHS[month-1]} {year}</p>
              </div>
              <div className="flex gap-2">
                {deployments.map(dep => (
                  <button key={dep.id}
                    onClick={() => generateMutation.mutate(dep.id)}
                    disabled={generateMutation.isPending}
                    className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 font-medium flex items-center gap-1">
                    <RefreshCw size={12} className={generateMutation.isPending ? 'animate-spin' : ''} />
                    {dep.candidates?.first_name} {dep.candidates?.last_name?.charAt(0)}.
                  </button>
                )).slice(0, 5)}
                {deployments.length > 5 && (
                  <span className="text-xs text-gray-400 self-center">+{deployments.length - 5} more</span>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Timesheets — {MONTHS[month - 1]} {year}
              </p>
            </div>

            {loadingTs ? (
              <div className="text-center py-12 text-gray-400">Loading...</div>
            ) : !tsData?.timesheets?.length ? (
              <div className="text-center py-16">
                <FileText size={32} className="mx-auto text-gray-200 mb-3" />
                <p className="text-gray-400 text-sm">No timesheets for this period. Generate them above.</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-gray-50">
                  <tr>
                    {['Worker', 'Client', 'Present', 'Absent', 'Leave', 'OT Hrs', 'Status', 'Actions'].map(h => (
                      <th key={h} className="text-left text-xs font-medium text-gray-400 px-5 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {tsData.timesheets.map(ts => (
                    <tr key={ts.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3 text-sm font-medium text-gray-900">
                        {ts.candidates?.first_name} {ts.candidates?.last_name}
                        <p className="text-xs text-gray-400 font-normal">{ts.candidates?.employee_code}</p>
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-600">{ts.clients?.company_name}</td>
                      <td className="px-5 py-3 text-sm text-green-600 font-medium">{ts.present_days}</td>
                      <td className="px-5 py-3 text-sm text-red-500">{ts.absent_days}</td>
                      <td className="px-5 py-3 text-sm text-purple-600">{ts.leave_days}</td>
                      <td className="px-5 py-3 text-sm text-blue-600">{Number(ts.overtime_hours).toFixed(1)}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${tsStatusColor(ts.status)}`}>
                          {ts.status?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex gap-2">
                          {ts.status === 'draft' && (
                            <button onClick={() => submitTsMutation.mutate(ts.id)} disabled={submitTsMutation.isPending}
                              className="text-xs px-2.5 py-1 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 font-medium">
                              Submit
                            </button>
                          )}
                          {ts.status === 'submitted' && isAdmin && (
                            <button onClick={() => approveTsMutation.mutate(ts.id)} disabled={approveTsMutation.isPending}
                              className="text-xs px-2.5 py-1 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 font-medium">
                              Approve
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      {selectedDep && <MarkAttendanceModal deployment={selectedDep} onClose={() => setSelectedDep(null)} />}
      {showLeaveModal && <ApplyLeaveModal deployments={deployments} onClose={() => setShowLeaveModal(false)} />}
    </div>
  );
};

export default Attendance;
